(() => {
  const tabs = document.querySelectorAll('.product-tabs__button');
  const panels = document.querySelectorAll('.product-tabs__panel');

  const escapeHtml = (value) => value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const renderInlineMarkdown = (value) => escapeHtml(value)
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');

  const normalizeText = (value) => (value || '')
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const getPanelName = (content) => content.closest('[data-panel]')?.dataset.panel || '';

  const parseKeyValueLines = (lines) => lines
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const cleaned = line.replace(/^[-•]\s+/, '').trim();
      const separator = cleaned.indexOf(':');
      if (separator < 1) return null;
      const label = cleaned.slice(0, separator).trim();
      const value = cleaned.slice(separator + 1).trim();
      if (!label || !value) return null;
      return { label, value };
    })
    .filter(Boolean);

  const dashSectionHeadings = [
    'Benefits',
    'Key Features',
    'Telephony Features',
    'Interfaces',
    'Display',
    'Camera & Video',
    'Audio Features',
    'Security',
    'Management',
    'Power',
    'Physical',
    'Connectivity',
    'Controls',
    'Ideal For',
    'Package Contents',
    "What's in the Box",
    'Whats in the Box',
    'Included',
    'In the Box',
    'Hardware Specifications',
    'System Performance',
    'Performance',
    'Network',
    'Wireless',
    'Video',
    'Audio',
    'Storage',
    'Compatibility',
    'Installation',
    'General',
    'Ports',
    'Firewall',
    'IPS',
    'NGFW',
    'Threat Protection',
    'VPN Performance',
    'Concurrent Sessions',
    'Specifications'
  ];

  const dashSectionHeadingsLower = dashSectionHeadings.map((heading) => heading.toLowerCase());

  const getDashHeading = (value) => {
    const cleaned = normalizeText(value).replace(/:$/, '');
    const exactIndex = dashSectionHeadingsLower.indexOf(cleaned.toLowerCase());
    if (exactIndex >= 0) return { heading: dashSectionHeadings[exactIndex], rest: '' };

    const leading = dashSectionHeadings.find((heading) => {
      const pattern = new RegExp(`^${heading.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?:\\s*[:\\n]\\s*|\\s{2,}|\\s+)`, 'i');
      return pattern.test(value);
    });

    if (!leading) return null;

    const rest = value.replace(new RegExp(`^${leading.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?:\\s*[:\\n]\\s*|\\s{2,}|\\s+)`, 'i'), '').trim();
    return { heading: leading, rest };
  };

  const getTableLabels = (panelName) => {
    if (panelName === 'specs') {
      return ['Specification area', 'Specification', 'Details'];
    }

    if (panelName === 'box') {
      return ['Area', 'Item', 'Details'];
    }

    return ['Feature area', 'Feature', 'Details'];
  };

  const buildStructuredRowsTable = (content, rows, introHtml = '') => {
    const panelName = getPanelName(content);
    const [areaLabel, featureLabel, detailLabel] = getTableLabels(panelName);
    const table = document.createElement('table');
    table.className = 'product-feature-table';
    table.dataset.featuresTable = 'true';
    table.innerHTML = `
      <thead>
        <tr>
          <th>${escapeHtml(areaLabel)}</th>
          <th>${escapeHtml(featureLabel)}</th>
          <th>${escapeHtml(detailLabel)}</th>
        </tr>
      </thead>
      <tbody>
        ${rows.map((row) => `
          <tr>
            <td>${escapeHtml(row.category)}</td>
            <td><strong>${escapeHtml(row.feature)}</strong></td>
            <td>${escapeHtml(row.detail || '')}</td>
          </tr>
        `).join('')}
      </tbody>
    `;

    content.innerHTML = introHtml;
    content.append(table);
    content.dataset.formatted = 'true';
  };

  const parseMarkdownCells = (line) => line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((cell) => normalizeText(cell))
    .filter(Boolean);

  const buildMarkdownTable = (content, rawText) => {
    if (!content || content.querySelector('table')) return false;

    const source = (rawText || '')
      .replace(/\u00a0/g, ' ')
      .replace(/\r/g, '')
      .replace(/\\r/g, '')
      .replace(/\\n/g, '\n')
      .trim();

    if (!source.includes('|')) return false;

    const hasMarkdownSeparator = source.split('\n').some((line) =>
      /^\s*\|?\s*:?-{3,}:?\s*(?:\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(line)
    );
    if (!hasMarkdownSeparator) return false;

    let rows = source
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.includes('|'))
      .map(parseMarkdownCells)
      .filter((cells) => cells.length >= 2);

    rows = rows.filter((cells) => !cells.every((cell) => /^:?-{3,}:?$/.test(cell.replace(/\s/g, ''))));
    if (rows.length < 2) return false;

    const headers = rows[0];
    const bodyRows = rows.slice(1).filter((cells) => cells.length);
    if (!headers.length || !bodyRows.length) return false;

    const table = document.createElement('table');
    table.className = 'product-feature-table';
    table.dataset.markdownTable = 'true';
    table.innerHTML = `
      <thead>
        <tr>${headers.map((header) => `<th>${escapeHtml(header)}</th>`).join('')}</tr>
      </thead>
      <tbody>
        ${bodyRows.map((row) => `
          <tr>
            ${headers.map((_, index) => `<td>${index === 1 ? `<strong>${escapeHtml(row[index] || '')}</strong>` : escapeHtml(row[index] || '')}</td>`).join('')}
          </tr>
        `).join('')}
      </tbody>
    `;

    content.innerHTML = '';
    content.append(table);
    content.dataset.formatted = 'true';
    return true;
  };

  const buildDashSeparatedTable = (content, rawText) => {
    if (!content || content.querySelector('table')) return false;

    const panelName = getPanelName(content);
    if (!['features', 'specs', 'deployment', 'box'].includes(panelName)) return false;

    const source = (rawText || '')
      .replace(/\u00a0/g, ' ')
      .replace(/\r/g, '')
      .replace(/\\r/g, '')
      .replace(/\\n/g, '\n')
      .replace(/[–—]/g, ' - ')
      .replace(/\s+-\s+/g, ' - ')
      .trim();

    if (!/\s-\s/.test(source)) return false;

    const tokens = source.split(/\s-\s/)
      .map((item) => item.trim())
      .filter(Boolean);

    if (tokens.length < 4) return false;

    const defaultCategory = panelName === 'specs' ? 'Specifications' : 'Key Features';
    const intro = [];
    const rows = [];
    let currentCategory = defaultCategory;

    tokens.forEach((token, index) => {
      const headingMatch = getDashHeading(token);
      if (headingMatch) {
        currentCategory = headingMatch.heading;

        if (headingMatch.rest && headingMatch.rest.length > 30) {
          intro.push(`<p><strong>${escapeHtml(headingMatch.heading)}</strong> ${renderInlineMarkdown(headingMatch.rest)}</p>`);
        }

        return;
      }

      if (index === 0 && token.length > 120) {
        intro.push(`<p>${renderInlineMarkdown(token)}</p>`);
        return;
      }

      const keyValue = token.match(/^([^:]{2,60}):\s*(.+)$/);
      if (keyValue) {
        rows.push({
          category: currentCategory,
          feature: keyValue[1].trim(),
          detail: keyValue[2].trim()
        });
        return;
      }

      rows.push({
        category: currentCategory,
        feature: token,
        detail: ''
      });
    });

    if (rows.length < 3) return false;

    buildStructuredRowsTable(content, rows, intro.join(''));
    return true;
  };

  const splitSentencesIntoParagraphs = (value) => {
    const sentences = value
      .replace(/\s+/g, ' ')
      .split(/(?<=[.!?])\s+(?=[A-Z0-9])/)
      .map((item) => item.trim())
      .filter(Boolean);

    if (sentences.length <= 1) return [value.trim()].filter(Boolean);

    const paragraphs = [];
    for (let index = 0; index < sentences.length; index += 2) {
      paragraphs.push(sentences.slice(index, index + 2).join(' '));
    }

    return paragraphs;
  };

  const buildOverviewNarrative = (content, rawText) => {
    if (!content || getPanelName(content) !== 'overview' || content.querySelector('table')) return false;

    const source = (rawText || '')
      .replace(/\u00a0/g, ' ')
      .replace(/\r/g, '')
      .replace(/\\r/g, '')
      .replace(/\\n/g, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();

    if (source.length < 160) return false;

    const idealMatch = source.match(/\bIdeal For\b/i);
    const prose = idealMatch ? source.slice(0, idealMatch.index).trim() : source;
    const idealText = idealMatch ? source.slice(idealMatch.index + idealMatch[0].length).trim() : '';
    const proseParagraphs = prose.includes('\n\n')
      ? prose.split(/\n\s*\n/).map((item) => item.trim()).filter(Boolean)
      : splitSentencesIntoParagraphs(prose);

    let html = '<h3>Product overview</h3>';
    html += proseParagraphs.map((paragraph) => `<p>${renderInlineMarkdown(paragraph)}</p>`).join('');

    if (idealText) {
      const idealItems = idealText
        .replace(/\n/g, ' - ')
        .split(/\s+-\s+/)
        .map((item) => item.replace(/^[-•]\s*/, '').replace(/\.$/, '').trim())
        .filter(Boolean);

      if (idealItems.length) {
        html += '<h3>Ideal For</h3>';
        html += `<ul>${idealItems.map((item) => `<li>${renderInlineMarkdown(item)}</li>`).join('')}</ul>`;
      }
    }

    content.innerHTML = html;
    content.dataset.formatted = 'true';
    return true;
  };

  const buildKeyValueTableFromRows = (content, rows) => {
    const table = document.createElement('table');
    table.className = 'product-key-value-table';
    table.dataset.keyValueTable = 'true';
    table.innerHTML = `
      <tbody>
        ${rows.map((row) => `
          <tr>
            <th scope="row">${escapeHtml(row.label)}</th>
            <td>${escapeHtml(row.value)}</td>
          </tr>
        `).join('')}
      </tbody>
    `;

    content.innerHTML = '';
    content.append(table);
    content.dataset.formatted = 'true';
  };

  const cleanEscapedBreakNodes = (content) => {
    const walker = document.createTreeWalker(content, NodeFilter.SHOW_TEXT);
    const removals = [];

    while (walker.nextNode()) {
      const node = walker.currentNode;
      const normalized = node.nodeValue
        .replace(/\\r/g, '')
        .replace(/\\n/g, '\n');

      if (!normalized.trim()) {
        removals.push(node);
      } else if (normalized !== node.nodeValue) {
        node.nodeValue = normalized;
      }
    }

    removals.forEach((node) => node.remove());
  };

  const buildKeyValueTable = (content) => {
    if (!content || content.querySelector('table')) return false;
    const panelName = getPanelName(content);
    if (!['overview', 'specs', 'deployment'].includes(panelName)) return false;

    const rows = [];
    const sourceItems = Array.from(content.querySelectorAll('li, p'))
      .filter((item) => normalizeText(item.textContent).includes(':'));

    sourceItems.forEach((item) => {
      const strong = item.querySelector('strong, b');
      let label = '';
      let value = '';

      if (strong && normalizeText(strong.textContent).includes(':')) {
        label = normalizeText(strong.textContent).replace(/:$/, '');
        const clone = item.cloneNode(true);
        const cloneStrong = clone.querySelector('strong, b');
        if (cloneStrong) cloneStrong.remove();
        value = normalizeText(clone.textContent).replace(/^:\s*/, '');
      } else {
        const text = normalizeText(item.textContent);
        const separator = text.indexOf(':');
        label = text.slice(0, separator).trim();
        value = text.slice(separator + 1).trim();
      }

      if (label && value) rows.push({ label, value });
    });

    if (rows.length < 3) return false;

    buildKeyValueTableFromRows(content, rows);
    return true;
  };

  const buildOverviewSpecSummary = () => {
    const tabsRoot = document.querySelector('.product-tabs');
    if (!tabsRoot) return;

    const overviewPanel = tabsRoot.querySelector('[data-panel="overview"] .rte');
    const specsTable = tabsRoot.querySelector('[data-panel="specs"] table');
    if (!overviewPanel || !specsTable || overviewPanel.querySelector('[data-overview-spec-summary]')) return;

    const rows = Array.from(specsTable.querySelectorAll('tr'));
    const getRowValue = (labelMatcher) => {
      const row = rows.find((item) => {
        const firstCell = item.querySelector('th, td');
        return firstCell && labelMatcher(normalizeText(firstCell.textContent).toLowerCase());
      });
      if (!row) return '';

      const cells = Array.from(row.children);
      return normalizeText(cells[1]?.textContent || cells[0]?.textContent || '');
    };

    const interfaceParts = [
      ['GE RJ45 WAN', getRowValue((text) => text.includes('ge rj45 wan'))],
      ['GE RJ45', getRowValue((text) => text === 'ge rj45 ports' || text.includes('ge rj45 ports'))],
      ['GE SFP', getRowValue((text) => text.includes('ge sfp slots'))],
      ['management/HA', getRowValue((text) => text.includes('management/ha'))]
    ]
      .filter(([, value]) => value && value !== '-' && value !== '0')
      .map(([label, value]) => `${value} ${label}`);

    const summaryItems = [
      ['IPS', getRowValue((text) => text.includes('ips throughput'))],
      ['NGFW', getRowValue((text) => text.includes('ngfw throughput'))],
      ['Threat Protection', getRowValue((text) => text.includes('threat protection throughput'))],
      ['Interfaces', interfaceParts.join(', ')]
    ].filter(([, value]) => value);

    if (summaryItems.length < 2) return;

    const table = document.createElement('table');
    table.className = 'product-overview-spec-summary';
    table.dataset.overviewSpecSummary = 'true';
    table.innerHTML = `
      <thead>
        <tr>${summaryItems.map(([label]) => `<th>${escapeHtml(label)}</th>`).join('')}</tr>
      </thead>
      <tbody>
        <tr>${summaryItems.map(([, value]) => `<td>${escapeHtml(value)}</td>`).join('')}</tr>
      </tbody>
    `;

    const firstHeading = overviewPanel.querySelector('h2, h3, h4');
    if (firstHeading) {
      firstHeading.before(table);
    } else {
      overviewPanel.append(table);
    }
  };

  const buildFeaturesTable = () => {
    const featuresPanel = document.querySelector('.product-tabs [data-panel="features"] .rte');
    if (!featuresPanel || featuresPanel.querySelector('[data-features-table]')) return;

    const rows = [];
    let currentCategory = 'Key Features';

    Array.from(featuresPanel.children).forEach((child) => {
      const tagName = child.tagName?.toLowerCase();
      if (tagName === 'h3' || tagName === 'h4') {
        currentCategory = normalizeText(child.textContent).replace(/:$/, '') || currentCategory;
        return;
      }

      if (tagName !== 'ul' && tagName !== 'ol') return;

      Array.from(child.querySelectorAll(':scope > li')).forEach((item) => {
        const strong = item.querySelector('strong, b');
        const category = currentCategory;
        let feature = strong ? normalizeText(strong.textContent).replace(/:$/, '') : '';
        let detail = '';

        if (strong) {
          const clone = item.cloneNode(true);
          const cloneStrong = clone.querySelector('strong, b');
          if (cloneStrong) cloneStrong.remove();
          detail = normalizeText(clone.textContent).replace(/^:\s*/, '');
        } else {
          feature = normalizeText(item.textContent);
        }

        if (feature || detail) {
          rows.push({ category, feature, detail });
        }
      });
    });

    if (rows.length < 2) return;

    const table = document.createElement('table');
    table.className = 'product-feature-table';
    table.dataset.featuresTable = 'true';
    table.innerHTML = `
      <thead>
        <tr>
          <th>Feature area</th>
          <th>Feature</th>
          <th>Details</th>
        </tr>
      </thead>
      <tbody>
        ${rows.map((row) => `
          <tr>
            <td>${escapeHtml(row.category)}</td>
            <td><strong>${escapeHtml(row.feature)}</strong></td>
            <td>${escapeHtml(row.detail)}</td>
          </tr>
        `).join('')}
      </tbody>
    `;

    featuresPanel.innerHTML = '';
    featuresPanel.append(table);
    featuresPanel.dataset.formatted = 'true';
  };

  const formatRichText = () => {
    document.querySelectorAll('.product-tabs .rte').forEach((content) => {
      if (content.dataset.formatted === 'true') return;
      cleanEscapedBreakNodes(content);
      if (content.querySelector('table, ul, ol, h2, h3, h4, img')) {
        content.dataset.formatted = 'true';
        return;
      }

      const rawText = (content.textContent || '')
        .replace(/\u00a0/g, ' ')
        .replace(/\r/g, '')
        .replace(/\\r/g, '')
        .replace(/\\n/g, '\n')
        .replace(/\n\s*\n/g, '\n\n')
        .trim();

      if (buildMarkdownTable(content, rawText)) return;

      const hasBrokenFormatting = rawText.includes('**')
        || rawText.includes('\\n')
        || rawText.includes('\n')
        || parseKeyValueLines(rawText.split('\n')).length >= 3;
      if (!hasBrokenFormatting) return;

      const lines = rawText.split('\n');
      const panelName = getPanelName(content);

      let html = '';
      let paragraph = [];
      let list = [];
      let listMode = false;

      const flushParagraph = () => {
        if (!paragraph.length) return;
        html += `<p>${paragraph.map(renderInlineMarkdown).join(' ')}</p>`;
        paragraph = [];
      };

      const flushList = () => {
        if (!list.length) return;
        html += `<ul>${list.map((item) => `<li>${renderInlineMarkdown(item)}</li>`).join('')}</ul>`;
        list = [];
      };

      lines.forEach((line) => {
        const trimmed = line.trim();
        if (!trimmed || trimmed === '.' || trimmed === '\\n') {
          flushParagraph();
          if (!listMode) flushList();
          return;
        }

        const markdownHeading = trimmed.match(/^\*\*([^*]+)\*\*\.?$/);
        const plainHeading = trimmed.match(/^(Ideal For|Benefits|Key Features|Features|Specifications|Shipping|What's Included|Whats in the Box|What's in the Box|Included|In the Box)$/i);
        const uppercaseHeading = trimmed.length <= 70
          && /^[A-Z0-9 &/()+-]+:?$/.test(trimmed)
          && /[A-Z]/.test(trimmed)
          && !trimmed.includes(':');

        if (markdownHeading || plainHeading || uppercaseHeading) {
          flushParagraph();
          flushList();
          html += `<h3>${escapeHtml(markdownHeading ? markdownHeading[1] : (plainHeading ? plainHeading[1] : trimmed.replace(/:$/, '')))}</h3>`;
          listMode = true;
          return;
        }

        const cleaned = trimmed
          .replace(/^[-•]\s+/, '')
          .replace(/^\\n\s*/, '')
          .replace(/\.$/, '');

        if (listMode && cleaned.length < 140) {
          list.push(cleaned);
        } else {
          flushList();
          listMode = false;
          paragraph.push(trimmed);
        }
      });

      flushParagraph();
      flushList();

      if (html) {
        content.innerHTML = html;
        content.dataset.formatted = 'true';
      }
    });
  };

  tabs.forEach(tab => {
    tab.addEventListener('click', () => {
      const target = tab.dataset.tab;
      tabs.forEach(t => { t.classList.remove('active'); t.setAttribute('aria-selected', 'false'); });
      panels.forEach(p => p.classList.remove('active'));
      tab.classList.add('active');
      tab.setAttribute('aria-selected', 'true');
      const panel = document.querySelector(`.product-tabs__panel[data-panel="${target}"]`);
      if (panel) panel.classList.add('active');
      formatRichText();
    });
  });

  formatRichText();
})();
